import {
  ApiPlatform,
  OnboardingServiceSignupAuthServiceEnum,
} from "@incendium/api";
import {
  ArrowForward,
  StorefrontOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import {
  loginWithSignupAuthResponse,
  socialAuth,
} from "features/signup/services/signupService";
import WooCommerceSignupLayout from "features/signup/woocommerce/WooCommerceSignupLayout";
import { motion } from "motion/react";
import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useNotification } from "Hooks";
import { useSearchParam } from "react-use";

const SHOPIFY_APP_URL = "https://apps.shopify.com/incendium-pixel";

const fieldMotion = (delay: number) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay },
});

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  padding: theme.spacing(1.5, 0.5),
  "& .MuiChip-icon": {
    color: theme.palette.primary.main,
  },
}));

function ShopifyStoreSetup() {
  return (
    <Stack spacing={3} alignItems="center" textAlign="center">
      <motion.div {...fieldMotion(0)}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
          }}
        >
          <StorefrontOutlined sx={{ fontSize: 36 }} />
        </Box>
      </motion.div>

      <motion.div {...fieldMotion(0.1)}>
        <Box>
          <Typography variant="h1" fontWeight={700}>
            Setting up your store
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            mt={0.75}
          >
            We're connecting your Shopify store to Incendium. This only takes a
            moment.
          </Typography>
        </Box>
      </motion.div>

      <motion.div {...fieldMotion(0.2)}>
        <CircularProgress size={40} />
      </motion.div>
    </Stack>
  );
}

function ShopifyInstallPrompt() {
  return (
    <Stack spacing={3}>
      <Box>
        <Typography align="center" variant="h1" fontWeight={700}>
          Start free with Incendium
        </Typography>
        <Typography
          align="center"
          variant="body1"
          color="text.secondary"
          mt={0.75}
        >
          Install the Incendium Shopify app to connect your store and unlock
          accurate first-party ecommerce analytics.
        </Typography>
      </Box>

      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1.5}
          mb={1}
        >
          <StyledChip
            label="2-min setup"
            size="small"
            icon={<TimerOutlined />}
          />
          <StyledChip
            label="Built for Shopify"
            size="small"
            icon={<StorefrontOutlined />}
          />
        </Stack>
      </Box>

      <motion.div {...fieldMotion(0.2)}>
        <Button
          fullWidth
          size="large"
          color="primary"
          href={SHOPIFY_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<ArrowForward />}
        >
          Install Shopify app
        </Button>
      </motion.div>

      <Typography variant="body2" color="text.secondary" textAlign="center">
        Already have an account?{" "}
        <Link
          component={RouterLink}
          to="/login"
          underline="hover"
          fontWeight={600}
        >
          Log in
        </Link>
      </Typography>
    </Stack>
  );
}

function ShopifySignupPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { showErrorNotification } = useNotification();
  const code = useSearchParam("code");
  const shop = useSearchParam("shop");

  const login = useCallback(
    async (code: string, shop: string) => {
      try {
        const response = await socialAuth({
          platform: ApiPlatform.PLATFORM_SHOPIFY,
          service: OnboardingServiceSignupAuthServiceEnum.SHOPIFY,
          shop,
          code,
        });

        await loginWithSignupAuthResponse(response, dispatch);
        history.push("/");
      } catch (error: any) {
        let errorMessage = "Failed to log in. Please try again.";
        try {
          const errorData = await error.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // keep default message
        }

        showErrorNotification(errorMessage);
      }
    },
    [dispatch, showErrorNotification, history],
  );

  useEffect(() => {
    if (code && shop) {
      login(code, shop);
    }
  }, [login, code, shop]);
  if (code) {
    return (
      <WooCommerceSignupLayout platformName="Shopify">
        <ShopifyStoreSetup />
      </WooCommerceSignupLayout>
    );
  }

  return (
    <WooCommerceSignupLayout platformName="Shopify">
      <ShopifyInstallPrompt />
    </WooCommerceSignupLayout>
  );
}

export default ShopifySignupPage;
